import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { SINGLE_LOCALE } from "./config";
import {
  SITE_NAME,
  alternatePath,
  seoForPath,
  siteOriginForLocale,
  socialImagesForLocale,
} from "./seo";

export const LOCALES = ["ru", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export const ACTIVE_LOCALES: readonly Locale[] = SINGLE_LOCALE
  ? [SINGLE_LOCALE]
  : LOCALES;

interface LocaleContextValue {
  locale: Locale;
  text: (ru: string, en: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function isLocale(value: string | undefined): value is Locale {
  return ACTIVE_LOCALES.some((locale) => locale === value);
}

export function preferredLocale(): Locale {
  if (SINGLE_LOCALE) return SINGLE_LOCALE;
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem("magic-squares-locale");
  if (isLocale(stored ?? undefined)) return stored as Locale;
  return window.navigator.languages.some((language) =>
    language.toLowerCase().startsWith("ru"),
  )
    ? "ru"
    : "en";
}

export function localePath(locale: Locale, target: string): string {
  if (target.startsWith("#") || /^[a-z]+:/i.test(target)) return target;
  if (!target.startsWith("/")) return target;
  if (SINGLE_LOCALE) {
    return (
      target.replace(/^\/(ru|en)(?=\/|$)/, "") || "/"
    );
  }
  if (/^\/(ru|en)(?:\/|$)/.test(target)) {
    return target.replace(/^\/(ru|en)(?=\/|$)/, `/${locale}`);
  }
  return target === "/" ? `/${locale}` : `/${locale}${target}`;
}

function replaceLocale(pathname: string, locale: Locale): string {
  if (SINGLE_LOCALE) return localePath(locale, pathname);
  if (/^\/(ru|en)(?:\/|$)/.test(pathname)) {
    return pathname.replace(/^\/(ru|en)(?=\/|$)/, `/${locale}`);
  }
  return localePath(locale, pathname);
}

function setAlternateLink(language: string, href: string) {
  const selector = `link[data-magic-squares-locale="${language}"]`;
  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement("link");
    link.rel = "alternate";
    link.hreflang = language;
    link.dataset.magicSquaresLocale = language;
    document.head.append(link);
  }
  link.href = href;
}

function setCanonicalLink(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(
    "link[data-magic-squares-canonical]",
  );
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    link.dataset.magicSquaresCanonical = "true";
    document.head.append(link);
  }
  link.href = href;
}

function setNamedMeta(name: string, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(
    `meta[name="${name}"]`,
  );
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.append(meta);
  }
  meta.content = content;
}

function setPropertyMeta(property: string, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`,
  );
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("property", property);
    document.head.append(meta);
  }
  meta.content = content;
}

function setStructuredData(schema: Readonly<Record<string, unknown>>) {
  let script = document.head.querySelector<HTMLScriptElement>(
    "script[data-magic-squares-schema]",
  );
  if (Object.keys(schema).length === 0) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.magicSquaresSchema = "true";
    document.head.append(script);
  }
  script.textContent = JSON.stringify(schema);
}

/* START_FUNCTION locale_provider */
/* START_CONTRACT
PURPOSE: Hydrate localized page content and keep runtime search metadata aligned with the active locale-owned route.
CONTRACT: Indexed routes receive one locale-owned canonical URL and reciprocal cross-domain alternates; non-indexed routes expose neither canonical nor alternate links.
FAILURE_MEANING: Client navigation may leave stale or duplicate search metadata in the document head.
KEYWORDS: locale, hydration, canonical, hreflang, metadata
COMPLEXITY: 3
END_CONTRACT */
export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  const location = useLocation();
  useEffect(() => {
    const metadata = seoForPath(location.pathname);
    const canonicalUrl = `${siteOriginForLocale(metadata.locale)}${metadata.canonicalPath}`;
    const socialImages = socialImagesForLocale(metadata.locale);
    window.localStorage.setItem("magic-squares-locale", locale);
    document.documentElement.lang = metadata.locale;
    document.title = metadata.title;
    setNamedMeta("description", metadata.description);
    setNamedMeta(
      "robots",
      metadata.index ? "index, follow" : "noindex, follow",
    );
    setNamedMeta("twitter:card", "summary_large_image");
    setNamedMeta("twitter:title", metadata.title);
    setNamedMeta("twitter:description", metadata.description);
    setNamedMeta("twitter:image", socialImages.twitter);
    setNamedMeta("twitter:image:alt", socialImages.alt);
    setPropertyMeta(
      "og:type",
      metadata.schema["@type"] === "Article" ? "article" : "website",
    );
    setPropertyMeta("og:site_name", SITE_NAME);
    setPropertyMeta("og:title", metadata.title);
    setPropertyMeta("og:description", metadata.description);
    setPropertyMeta("og:url", canonicalUrl);
    setPropertyMeta("og:locale", locale === "ru" ? "ru_RU" : "en_US");
    if (!SINGLE_LOCALE) {
      setPropertyMeta("og:locale:alternate", locale === "ru" ? "en_US" : "ru_RU");
    }
    setPropertyMeta("og:image", socialImages.openGraph);
    setPropertyMeta("og:image:secure_url", socialImages.openGraph);
    setPropertyMeta("og:image:type", "image/png");
    setPropertyMeta("og:image:width", "1200");
    setPropertyMeta("og:image:height", "630");
    setPropertyMeta("og:image:alt", socialImages.alt);

    if (metadata.index) {
      if (!SINGLE_LOCALE) {
        for (const language of LOCALES) {
          setAlternateLink(
            language,
            `${siteOriginForLocale(language)}${alternatePath(metadata.canonicalPath, language)}`,
          );
        }
        setAlternateLink(
          "x-default",
          `${siteOriginForLocale("en")}${alternatePath(metadata.canonicalPath, "en")}`,
        );
      }
      setCanonicalLink(canonicalUrl);
    } else {
      document.head
        .querySelectorAll(
          'link[rel="canonical"], link[rel="alternate"][hreflang]',
        )
        .forEach((link) => link.remove());
    }
    setStructuredData(metadata.schema);
  }, [locale, location.pathname]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      text: (ru, en) => (locale === "ru" ? ru : en),
    }),
    [locale],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}
/* END_FUNCTION locale_provider */

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("useLocale must be used inside LocaleProvider");
  return value;
}

export function switchLocalePath(pathname: string, locale: Locale): string {
  return replaceLocale(pathname, locale);
}
